import { useRef, useState } from "react";
import { toast } from "sonner";
import { Building2, ImagePlus, Loader2, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { CA, caErr } from "@/lib/clientAdmin";

export const Logo = ({ c, size = "h-10 w-10", testid }) => (c?.logo_url
  ? <img src={c.logo_url} alt={`${c.company} logo`} className={`${size} shrink-0 rounded-lg border border-[#27272A] bg-white/5 object-contain p-1`} data-testid={testid} />
  : <span className={`${size} flex shrink-0 items-center justify-center rounded-lg border border-[#27272A] bg-[#18181B] text-zinc-500`} data-testid={testid}><Building2 className="h-1/2 w-1/2" /></span>);

const OK = ["image/png", "image/jpeg", "image/svg+xml", "image/webp"];

// Upload / replace / remove a client company's logo (shown in their portal and emails).
export const LogoUpload = ({ c, onChange }) => {
  const inp = useRef();
  const [busy, setBusy] = useState(false);
  const up = async (f) => {
    if (!f) return;
    if (!OK.includes(f.type)) return toast.error("Use a PNG, JPG, SVG or WebP image.");
    if (f.size > 2 * 1024 * 1024) return toast.error("Keep the logo under 2 MB.");
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", f); const { data } = await api.post(`${CA}/${c.id}/logo`, fd); onChange(data.logo_url); toast.success("Logo updated"); }
    catch (e) { toast.error(caErr(e)); } finally { setBusy(false); }
  };
  const del = async () => { try { await api.delete(`${CA}/${c.id}/logo`); onChange(null); toast.success("Logo removed"); } catch (e) { toast.error(caErr(e)); } };
  return (
    <div className="flex items-center gap-3" data-testid="ca-logo">
      <Logo c={c} size="h-16 w-16" testid="ca-logo-img" />
      <div className="space-y-1">
        <button type="button" disabled={busy} onClick={() => inp.current?.click()} className="inline-flex items-center gap-1.5 rounded-full border border-[#27272A] px-3 py-1 text-xs text-zinc-200 hover:border-[#D4AF37]/60" data-testid="ca-logo-upload">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5 text-[#D4AF37]" />}{c.logo_url ? "Replace logo" : "Upload logo"}</button>
        {c.logo_url && <button type="button" onClick={del} className="ml-2 inline-flex items-center gap-1 text-[11px] text-zinc-500 hover:text-red-300" data-testid="ca-logo-remove"><Trash2 className="h-3 w-3" />Remove</button>}
        <p className="text-[10px] text-zinc-500">PNG, JPG, SVG or WebP · under 2 MB · shown in their portal and emails next to ours</p>
      </div>
      <input ref={inp} type="file" accept={OK.join(",")} className="hidden" onChange={(e) => { up(e.target.files?.[0]); e.target.value = ""; }} data-testid="ca-logo-file" />
    </div>
  );
};
